import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSplitDefined } from '@/api/getSplitDetails';
import chevronUpIcon from '@/assets/images/chevronUp.svg';
import { Price } from '@/components/Price/Price';
import {
  buildSplitBillItemRows,
  explodeBillToSplitItems,
} from '@/components/SplitByItemsMode/splitByItems.util';
import { useSplitDetails } from '@/hooks/useSplitDetails';
import { Position } from '@/interfaces/Bill';
import styles from '../Bill.module.scss';

interface Props {
  positions: Position[];
  grossDiscount: number;
  orderNumber: string;
}

export const BillSplitByItemsPositions = ({ positions, grossDiscount, orderNumber }: Props) => {
  const { t } = useTranslation();
  const { data: splitDetails } = useSplitDetails({ orderNumber });
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const lockedLineItemIds = isSplitDefined(splitDetails)
    ? splitDetails.lockedLineItemIds || []
    : [];
  const rows = buildSplitBillItemRows(
    explodeBillToSplitItems(positions, grossDiscount),
    lockedLineItemIds
  );

  const toggleItem = useCallback((key: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  return (
    <div className={styles.items}>
      {rows.map((row, index) => (
        <div
          className={`${styles.positionSection} ${row.isLocked ? styles.struck : ''}`}
          key={row.key}
          data-testid={`bill-unit-${index}`}
        >
          <div className={styles.itemRow}>
            <div className={styles.quantity} data-testid={`bill-unit-${index}-quantity`}>
              {t('checkout.bill.quantity', { quantity: 1 })}
            </div>
            <div data-testid={`bill-unit-${index}-name`}>
              {row.item.name}
              {row.item.modifiers.length > 0 && (
                <button
                  className={`${styles.chevron} ${expandedItems[row.key] ? styles.open : ''}`}
                  onClick={() => {
                    toggleItem(row.key);
                  }}
                >
                  <img src={chevronUpIcon} alt={t('checkout.bill.collapseIcon')} />
                </button>
              )}
            </div>
            <div className={styles.item} data-testid={`bill-unit-${index}-price`}>
              <Price amount={row.item.amount} />
            </div>
          </div>
          <div
            className={`${styles.modifiersRow} ${expandedItems[row.key] ? styles.expanded : ''}`}
          >
            {/* The unit amount is fully-loaded (modifiers folded in), so modifier
                rows list names only — a price here would double-display */}
            {row.item.modifiers.map(({ name, quantity }) => (
              <div key={name} className={styles.modifier}>
                <div className={styles.modifierName}>
                  {name}
                  {quantity > 1 ? t('checkout.bill.modifierQuantity', { quantity }) : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
